import React, { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FileApi, EndeavorApi } from "../services/ApiService";
import { AiDocumentProcessingApi } from "../services/ai/AiDocumentProcessingApi";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext"; // ✅ NEW: Use DataContext for document types
import "./FileUpload.css";
import DocumentEndeavorApi from '../services/api/DocumentEndeavorAPI';
import { DocumentImagesApi } from '../services/ai/DocumentImagesApi';
import { UsageEventsTracker } from '../services/UsageEventsTracker';

function FileUpload({ onUploadComplete, onUploadError, endeavorId, scoutIQMode = false, onCollapseChange, showCollapseToggle = true }) {
  const { user } = useAuth();
  const { documentTypes, fetchDocumentTypes } = useData(); // ✅ NEW: Get cached document types from DataContext
  const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15MB UI limit (value corrected to match comment)
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDocTypes, setSelectedDocTypes] = useState({});
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [articleTypeId, setArticleTypeId] = useState(null);
  /* --- Multi-image bundle (stitch) state --- */
  const [combineImages, setCombineImages] = useState(false);
  const [bundleTitle, setBundleTitle] = useState("");
  const [bundleDescription, setBundleDescription] = useState("");
  const [bundleOCR, setBundleOCR] = useState(true);
  const [bundleStatus, setBundleStatus] = useState(null); // {phase:'uploading'|'error'|'done', message}
  const [bundleProgress, setBundleProgress] = useState({ phase: 0, total: 3 }); // progress tracking
  const [stitchedResult, setStitchedResult] = useState(null); // success card data
  const [imageThumbnails, setImageThumbnails] = useState({}); // {fileName: dataURL}
  const [dropErrors, setDropErrors] = useState([]); // [{id,name,message}]
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Course tagging (only shown when no endeavorId prop — i.e. Documents page upload)
  const [courseOptions, setCourseOptions] = useState([]); // [{id, name}]
  const [selectedCourseId, setSelectedCourseId] = useState(''); // bulk course tag
  const [fileCourseIds, setFileCourseIds] = useState({}); // per-file: {fileName: courseId}

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => {
      const newValue = !prev;
      if (onCollapseChange) {
        onCollapseChange(newValue);
      }
      return newValue;
    });
  }, [onCollapseChange]);

  // Load course options for the tag-to-course selector (only when no endeavorId prop)
  useEffect(() => {
    if (endeavorId) return; // already scoped to a course
    const uid = user?.user_id || user?.userId || user?.id;
    if (!uid) return;
    (async () => {
      try {
        const resp = await EndeavorApi.getEndeavors(uid, { page: 0, size: 200 });
        if (!resp?.ok) return;
        const data = await resp.json();
        const list = Array.isArray(data) ? data : (data.content || data.endeavors || []);
        const opts = list
          .map(e => ({ id: e.id || e.endeavorId, name: e.name || e.title || `Course ${e.id || e.endeavorId}` }))
          .filter(o => o.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        setCourseOptions(opts);
      } catch (e) {
        console.warn('FileUpload: failed to load courses', e);
      }
    })();
  }, [endeavorId, user]);

  // Emit a global event so any listeners (e.g. MyDocumentsCard) can refresh immediately.
  // Keep event name synchronized with UploadDocuments variant.
  const emitDocumentsChanged = useCallback((detail = {}) => {
    // Small intentional delay to let backend finish persisting & associating
    const DELAY_MS = detail?.delayMs != null ? detail.delayMs : 140; // tunable; minimal perceived latency
    try {
      setTimeout(() => {
        try {
          window.dispatchEvent(new CustomEvent('documents:changed', { detail }));
        } catch (_) {}
      }, DELAY_MS);
    } catch (_) {
      // no-op
    }
  }, []);

  useEffect(() => {
    // Ensure document types are fetched (will use cache if available)
    if (!documentTypes || documentTypes.length === 0) {
      console.log('[FileUpload] Document types not loaded, fetching from DataContext...');
      fetchDocumentTypes();
    } else {
      console.log('[FileUpload] Using cached document types from DataContext:', documentTypes.length);
      // Find Article type and store its ID
      const articleType = documentTypes.find(dt => dt.name === 'Article');
      if (articleType) {
        setArticleTypeId(articleType.id);
      } else {
        console.warn('Article document type not found in available types');
      }
    }
  }, [documentTypes, fetchDocumentTypes]);

  // helper: push new rejection messages (keeps last 5) with de-dup by name+message
  const pushDropRejections = (fileRejections = []) => {
    if (!fileRejections.length) return;
    const items = fileRejections.map(rej => {
      const sizeMB = (rej?.file?.size ?? 0) / (1024 * 1024);
      const tooLarge = rej.errors?.some(e => e.code === 'file-too-large');
      const message = tooLarge
        ? `File is too large (${isFinite(sizeMB) ? sizeMB.toFixed(1) : '?'} MB). Max 15MB.`
        : (rej.errors?.[0]?.message || 'File not accepted.');
      return { id: `${rej.file?.name || 'file'}-${Date.now()}-${Math.random()}`, name: rej.file?.name || 'Unknown file', message };
    });
    setDropErrors(prev => {
      const seen = new Set(prev.map(p => `${p.name}|${p.message}`));
      const dedupNew = items.filter(it => !seen.has(`${it.name}|${it.message}`));
      return [...prev, ...dedupNew].slice(-5);
    });
  };

  // auto-clear banner after a few seconds
  useEffect(() => {
    if (dropErrors.length === 0) return;
    const t = setTimeout(() => setDropErrors([]), 6000);
    return () => clearTimeout(t);
  }, [dropErrors]);

  /* Image detection helper (mime or extension) - defined early to avoid use-before-define */
  const imageExtRegex = /\.(jpe?g|png|tiff?|bmp|gif|webp)$/i;
  const isImage = useCallback((file) => {
    if (!file) return false;
    if (file.type && file.type.startsWith('image/')) return true;
    return imageExtRegex.test(file.name || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Generate thumbnail for image file */
  const generateThumbnail = useCallback((file) => {
    if (imageThumbnails[file.name]) return; // Already generated
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = 80;
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        // Calculate aspect ratio fit
        const scale = Math.min(size / img.width, size / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (size - w) / 2;
        const y = (size - h) / 2;
        
        // Draw centered
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, x, y, w, h);
        
        setImageThumbnails(prev => ({ ...prev, [file.name]: canvas.toDataURL() }));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, [imageThumbnails]);

  // Generate thumbnails for image files
  useEffect(() => {
    selectedFiles.forEach(({file}) => {
      if (isImage(file) && !imageThumbnails[file.name]) {
        generateThumbnail(file);
      }
    });
  }, [selectedFiles, imageThumbnails, generateThumbnail, isImage]);

  // Auto-clear success card after 5 seconds
  useEffect(() => {
    if (!stitchedResult) return;
    const t = setTimeout(() => setStitchedResult(null), 5000);
    return () => clearTimeout(t);
  }, [stitchedResult]);

  // Dropzone setup
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    maxSize: MAX_FILE_BYTES,
    onDrop: (acceptedFiles, fileRejections) => {
      const filesPlain = acceptedFiles.map((file) => ({ file }));
      setSelectedFiles((prev) => [...prev, ...filesPlain]);
      // Auto-enable combineImages if user just added multiple images ONLY
      if (!combineImages) {
        const all = [...selectedFiles, ...filesPlain];
        const imageCount = all.filter(o => isImage(o.file)).length;
        const nonImage = all.filter(o => !isImage(o.file)).length;
        if (imageCount >= 2 && nonImage === 0) {
          setCombineImages(true);
        }
      }
      // Show client-side size errors
      if (fileRejections?.length) {
        pushDropRejections(fileRejections);
        setErrorMessages(prev => {
          const next = { ...prev };
          for (const rej of fileRejections) {
            const tooLarge = rej.errors?.some(e => e.code === 'file-too-large');
            if (tooLarge) {
              next[rej.file.name] = `File is too large (${(rej.file.size/(1024*1024)).toFixed(1)} MB). Max 15MB.`;
            } else if (rej.errors?.length) {
              next[rej.file.name] = rej.errors[0].message || 'File not accepted.';
            }
          }
          return next;
        });
      }
    },
    onDropRejected: (rejections) => pushDropRejections(rejections),
    noClick: true,
    accept: {
      // Documents
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
      // Text-like files
      'text/*': ['.txt', '.md', '.markdown', '.csv', '.json', '.html', '.htm', '.rtf', '.log'],
      'application/json': ['.json'],
      'application/rtf': ['.rtf'],
      // Images (extended list)
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/tiff': ['.tif', '.tiff'],
      'image/bmp': ['.bmp'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
  });

  /* Derived state: when should stitch controls be visible? */
  const imageEntries = selectedFiles.filter(o => isImage(o.file));
  const nonImageEntries = selectedFiles.filter(o => !isImage(o.file));
  const showStitchOption = imageEntries.length >= 2 && nonImageEntries.length === 0;

  // Ensure combineImages can't stay on if criteria no longer met
  useEffect(() => {
    if (!showStitchOption && combineImages) {
      setCombineImages(false);
      setBundleStatus(null);
    }
  }, [showStitchOption, combineImages]);

  // Automatically apply Article type to all files
  useEffect(() => {
    if (!articleTypeId || selectedFiles.length === 0) return;
    const newSelectedDocTypes = { ...selectedDocTypes };
    for (const { file } of selectedFiles) {
      // Only set if not already set (preserve existing assignments)
      if (!newSelectedDocTypes[file.name]) {
        newSelectedDocTypes[file.name] = articleTypeId;
      }
    }
    setSelectedDocTypes(newSelectedDocTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, articleTypeId]);

  // Remove a file from the selected files list
  const removeFile = (fileName) => {
    setSelectedFiles((prev) => prev.filter(({ file }) => file.name !== fileName));
    setSelectedDocTypes((prev) => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });
    setFileCourseIds((prev) => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });
    setErrorMessages((prev) => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });
  };

  // Handle successful upload
  const onUploadSuccess = (data, fileName) => {
    console.log("Upload successful:", data);
    UsageEventsTracker.recordUserAction('DOCUMENT_UPLOAD_SUCCESS', {
      action: 'complete',
      category: 'document',
      entityType: 'document',
      entityId: data?.documentId || data?.document?.documentId,
      label: 'file_upload',
      metadata: { fileName, source: 'file_upload' }
    });
    if (onUploadComplete) {
      onUploadComplete(data);
    }
    // Broadcast so document lists elsewhere update
    emitDocumentsChanged({ source: 'FileUpload', fileName, documentId: data?.documentId || data?.document?.documentId });
    // Remove the successfully uploaded file from the list
    removeFile(fileName);
  };

  // ✅ NEW: Fire-and-forget AI processing function
  const startBackgroundAIProcessing = async (documentId, docTypeId, fileName) => {
    console.log(`FileUpload: Starting background FULL AI processing for document ${documentId}`);

    const fullPromise = AiDocumentProcessingApi.processDocumentFull(documentId, {
      documentTypeId: docTypeId || undefined
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json().catch(()=>({}));
        console.log(`FileUpload: Full processing completed for ${fileName}:`, data);
        return { type: 'full', success: true, data };
      } else {
        const errorText = await response.text().catch(()=>'<no body>');
        console.warn(`FileUpload: Full processing failed for ${fileName}:`, response.status, errorText);
        return { type: 'full', success: false, error: `${response.status}: ${errorText}` };
      }
    }).catch((error) => {
      console.warn(`FileUpload: Full processing error for ${fileName}:`, error);
      return { type: 'full', success: false, error: error.message };
    });

    Promise.allSettled([fullPromise]).then((results) => {
      const [fullResult] = results;
      console.log(`FileUpload: AI processing (full) completed for ${fileName}:`, fullResult.status === 'fulfilled' ? fullResult.value : { success: false, error: fullResult.reason });
      // Optional future: emit event about processing completion
    });
  };

  // ✅ UPDATED: Simplified upload process with fire-and-forget AI processing
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    // Check if user is available
    if (!user) {
      console.error('FileUpload: No user available for upload');
      setErrorMessages(prev => ({
        ...prev,
        general: 'User not authenticated. Please log in again.'
      }));
      return;
    }

    // Mark each file as uploading
    const newUploadingFiles = {};
    selectedFiles.forEach(({ file }) => {
      newUploadingFiles[file.name] = true;
    });
    setUploadingFiles((prev) => ({ ...prev, ...newUploadingFiles }));

    UsageEventsTracker.recordUserAction('DOCUMENT_UPLOAD_STARTED', {
      action: 'start',
      category: 'document',
      label: 'file_upload',
      value: selectedFiles.length,
      metadata: { fileCount: selectedFiles.length, combineImages, source: 'file_upload' }
    });

    /* --- Combined image bundle path --- */
    if (combineImages) {
      const imageEntries = selectedFiles.filter(o => isImage(o.file));
      const nonImageEntries = selectedFiles.filter(o => !isImage(o.file));
      if (!imageEntries.length) {
        setBundleStatus({ phase: 'error', message: 'No image files to combine.' });
        return;
      }
      if (!bundleTitle.trim()) {
        setBundleStatus({ phase: 'error', message: 'Title required for combined image document.' });
        return;
      }
      if (nonImageEntries.length) {
        // Option: warn user but continue with images only
        setBundleStatus({ phase: 'info', message: `Ignoring ${nonImageEntries.length} non-image file(s).` });
      }
      // Determine a unified documentTypeId for the combined document.
      // Strategy: gather doc types for image rows; if exactly one unique non-empty value use it;
      // else fall back to bulkDocType if set; else undefined (backend may infer or leave null).
      const imageDocTypeIds = imageEntries
        .map(e => selectedDocTypes[e.file.name])
        .filter(Boolean);
      const uniqueDocTypeIds = [...new Set(imageDocTypeIds)];
      let combinedDocTypeId = undefined;
      if (uniqueDocTypeIds.length === 1) {
        combinedDocTypeId = uniqueDocTypeIds[0];
      } else if (uniqueDocTypeIds.length === 0 && articleTypeId) {
        combinedDocTypeId = articleTypeId;
      } else if (uniqueDocTypeIds.length > 1) {
        console.warn('FileUpload: Multiple documentTypeIds among images; not setting a combined doc type. IDs:', uniqueDocTypeIds);
      }
      try {
        setBundleProgress({ phase: 1, total: 3 });
        setBundleStatus({ phase: 'uploading', message: 'Preparing images...' });
        // mark all image files as uploading for UI consistency
        const markUploading = {}; imageEntries.forEach(({file}) => markUploading[file.name]=true);
        setUploadingFiles(prev => ({...prev, ...markUploading}));
        
        console.debug('FileUpload (combineImages): initiating uploadCombinedImages', {
          images: imageEntries.map(e => ({ name: e.file.name, size: e.file.size, type: e.file.type })),
          title: bundleTitle.trim(),
          descriptionLength: bundleDescription.trim().length,
          ocr: bundleOCR,
          userId: user?.user_id || user?.userId || user?.id,
          combinedDocTypeId,
          perImageDocTypes: imageEntries.map(e => ({ file: e.file.name, docTypeId: selectedDocTypes[e.file.name] || null }))
        });
        
        // Update status before API call
        setBundleStatus({ phase: 'uploading', message: `Processing ${imageEntries.length} images with OCR...` });
        setBundleProgress({ phase: 2, total: 3 });
        
        const resp = await DocumentImagesApi.uploadCombinedImages(imageEntries.map(e=>e.file), {
          title: bundleTitle.trim(),
            description: bundleDescription.trim(),
            ocr: bundleOCR,
            processType: 'none',
            skipProcessing: false,
            userId: user?.user_id || user?.userId || user?.id,
            documentTypeId: combinedDocTypeId
        });
        if (!resp || !resp.ok) {
          let text='';
          try { text = await resp.text(); } catch {}
          throw new Error(text || 'Combined image upload failed');
        }
        
        // Update to final phase while processing response
        setBundleStatus({ phase: 'uploading', message: 'Finalizing document...' });
        setBundleProgress({ phase: 3, total: 3 });
        
        const data = await resp.json().catch(()=>({}));
        console.debug('FileUpload (combineImages): upload success response', data);
        // Extract stitched documentId (contract: data.document.documentId)
        const stitchedDocumentId = data?.document?.documentId || null;
        if (!stitchedDocumentId) {
          console.warn('FileUpload (combineImages): Unexpected stitched upload response shape, documentId missing', data);
        }

        // Associate with endeavor if applicable
        const effectiveCombinedEid = endeavorId || selectedCourseId;
        if (stitchedDocumentId && effectiveCombinedEid) {
          try {
            // Await association so the subsequent list refresh sees the relationship
            await DocumentEndeavorApi.associateDocumentWithEndeavor(stitchedDocumentId, effectiveCombinedEid);
          } catch(e) {
            console.warn('Association error (stitched doc)', e);
          }
        }

        // Fire-and-forget AI processing (metadata + RAG) like single uploads
        if (stitchedDocumentId) {
          try {
            startBackgroundAIProcessing(stitchedDocumentId, combinedDocTypeId, bundleTitle.trim() || 'Combined Image Document');
          } catch(e) {
            console.warn('Failed to start AI processing for stitched document', e);
          }
        }

        setBundleStatus({ phase: 'done', message: 'Combined image document uploaded.' });
        setBundleProgress({ phase: 0, total: 3 });
        
        // Set success result for display card
        setStitchedResult({
          filename: data?.document?.filename || bundleTitle.trim() + '.pdf',
          pageCount: imageEntries.length,
          ocrCharCount: data?.document?.ocrCharCount || 0,
          documentId: stitchedDocumentId
        });
        
        // Clear only the images from list
        setSelectedFiles(prev => prev.filter(o => !isImage(o.file)));
        // Clear thumbnails for removed images
        setImageThumbnails(prev => {
          const next = { ...prev };
          imageEntries.forEach(({file}) => delete next[file.name]);
          return next;
        });
        imageEntries.forEach(({file}) => {
          setUploadingFiles(prev => { const u = {...prev}; delete u[file.name]; return u; });
        });
        // Fire completion callback once with stitched document data + AI started indicator
        onUploadComplete && onUploadComplete({
          ...data,
          documentId: stitchedDocumentId,
          aiProcessingStatus: stitchedDocumentId ? 'started' : 'not-started'
        });
        // Emit one consolidated event for the stitched document (after association) with slight delay
        emitDocumentsChanged({ source: 'FileUpload', mode: 'combined-images', documentId: stitchedDocumentId });
        setBundleTitle(''); setBundleDescription('');
      } catch (err) {
        console.error('Combined image upload failed', err);
        setBundleStatus({ phase: 'error', message: err.message || 'Combined image upload failed' });
      }
      return; // do not proceed with individual uploads in this run
    }

    // Upload each file individually (legacy path)
    for (const { file } of selectedFiles) {
      // Client guard (in case file bypassed dropzone limit)
      if (file.size > MAX_FILE_BYTES) {
        setErrorMessages(prev => ({
          ...prev,
          [file.name]: `File is too large (${(file.size/(1024*1024)).toFixed(1)} MB). Max 15MB.`
        }));
        setUploadingFiles(prev => { const u = { ...prev }; delete u[file.name]; return u; });
        continue;
      }
      try {
        // Get the chosen doc type ID (if any)
        const docTypeId = selectedDocTypes[file.name];

        // Use FileApi metadata format — per-file course tag takes priority
        const effectiveEndeavorId = endeavorId || fileCourseIds[file.name] || selectedCourseId || undefined;
        const metadata = {
          name: file.name,
          documentTypeId: docTypeId || undefined,
          endeavorId: effectiveEndeavorId,
          userId: user.user_id || user.userId || user.id,
          description: `Uploaded file: ${file.name}`
        };

        console.log('FileUpload: Uploading file with FileApi:', { fileName: file.name, metadata });

        // Upload the file
        const uploadResponse = await FileApi.uploadFile(file, metadata);

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          console.log('FileUpload: File upload successful:', uploadData);

          // Remove uploading status immediately
          setUploadingFiles((prev) => {
            const updated = { ...prev };
            delete updated[file.name];
            return updated;
          });

          // ✅ UPDATED: Extract documentId from upload response
          let documentId = null;
          
          // Try different possible response formats
          if (uploadData.documentId) {
            documentId = uploadData.documentId;
          } else if (uploadData.document && uploadData.document.documentId) {
            documentId = uploadData.document.documentId;
          } else if (uploadData.document && uploadData.document.document_id) {
            documentId = uploadData.document.document_id;
          } else if (uploadData.id) {
            documentId = uploadData.id;
          }

          // ✅ FIRE-AND-FORGET: Start background AI processing without waiting
          if (documentId) {
            // Associate with endeavor (await so event sees it)
            const effectiveEid = endeavorId || fileCourseIds[file.name] || selectedCourseId;
            if (effectiveEid) {
              try {
                await DocumentEndeavorApi.associateDocumentWithEndeavor(documentId, effectiveEid);
              } catch(e) {
                console.warn('Association error', e);
              }
            }
            // Start AI processing (does not block UI)
            startBackgroundAIProcessing(documentId, docTypeId, file.name);
          } else {
            console.warn("FileUpload: No documentId found in upload response:", uploadData);
          }

          // ✅ IMMEDIATE (post-association): Call success callback right after upload (don't wait for AI processing)
          onUploadSuccess({
            ...uploadData,
            documentId: documentId,
            aiProcessingStatus: 'started' // Indicate that AI processing has been initiated
          }, file.name);

        } else {
          // Parse backend error (handles 413 with JSON body)
          let friendly = 'Upload failed.';
          try {
            const data = await uploadResponse.clone().json();
            if (uploadResponse.status === 413 ||
                /maximum upload size/i.test(data?.message || '') ||
                /exceeds the maximum/i.test(data?.error || '')) {
              friendly = `File is too large. Max 15MB. Server refused the upload${data?.maxAllowedSize ? ` (reported limit ${data.maxAllowedSize}).` : '.'}`;
            } else if (data?.error) {
              friendly = data.error;
            } else if (data?.message) {
              friendly = data.message;
            }
          } catch {
            const text = await uploadResponse.text();
            if (uploadResponse.status === 413 || /maximum upload size/i.test(text)) {
              friendly = 'File is too large. Max 15MB. Server refused the upload.';
            } else if (text) {
              friendly = text;
            }
          }
          throw new Error(friendly);
        }
      } catch (err) {
        console.error("FileUpload: Upload error:", err);
        const errorMessage = err.message || "An unexpected error occurred.";
        setErrorMessages(prev => ({ ...prev, [file.name]: errorMessage }));
        onUploadError && onUploadError(errorMessage, file.name);
        setUploadingFiles(prev => { const u = { ...prev }; delete u[file.name]; return u; });
      }
    }
  };

  return (
    <div className={`file-upload-container ${scoutIQMode ? 'scoutiq-upload-files-card' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
      {!scoutIQMode && (
        <div className="upload-header">
          <h2 className="upload-heading">Upload Files</h2>
          {showCollapseToggle && (
            <button 
              className="collapse-toggle"
              onClick={toggleCollapse}
              aria-label={isCollapsed ? "Expand upload panel" : "Collapse upload panel"}
              aria-expanded={!isCollapsed}
            >
              {isCollapsed ? '▼' : '▲'}
            </button>
          )}
        </div>
      )}
      
      <div className="upload-content" style={{ display: isCollapsed ? 'none' : 'block' }}>
      {/* Show error if user not authenticated */}
      {!user && (
        <div className="error-message" style={{
          padding: '1rem',
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)',
          color: 'var(--intent-danger-text, #721c24)',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid var(--intent-danger-border, #f5c6cb)'
        }}>
          Please log in to upload files.
        </div>
      )}

      {/* Show general error messages */}
      {errorMessages.general && (
        <div className="error-message" style={{
          padding: '1rem',
          backgroundColor: 'var(--intent-danger-bg, #f8d7da)',
          color: 'var(--intent-danger-text, #721c24)',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid var(--intent-danger-border, #f5c6cb)'
        }}>
          {errorMessages.general}
        </div>
      )}

      {scoutIQMode ? (
        // ScoutIQ layout: centered clean upload experience
        <div className="scoutiq-upload-centered">
          <div
            {...getRootProps()}
            className={`scoutiq-dropzone ${isDragActive ? "scoutiq-dropzone--active" : ""}`}
          >
            <input {...getInputProps()} />
            {/* Glowing upload glyph */}
            <div className="scoutiq-dropzone__glyph" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            {isDragActive ? (
              <p className="scoutiq-dropzone__text">Drop files here...</p>
            ) : (
              <p className="scoutiq-dropzone__text">
                Drag &amp; drop files here
              </p>
            )}
            <p className="scoutiq-dropzone__subtext">PDF, DOCX, PPTX, TXT, Markdown, CSV</p>
          </div>

          <div className="scoutiq-divider">
            <span className="scoutiq-divider__line" />
            <span className="scoutiq-divider__text">or</span>
            <span className="scoutiq-divider__line" />
          </div>

          <button type="button" onClick={open} className="scoutiq-select-btn" disabled={!user}>
            Select Files
          </button>

          <div className="scoutiq-formats">
            <span className="scoutiq-format-tag">PDF</span>
            <span className="scoutiq-format-tag">DOCX</span>
            <span className="scoutiq-format-tag">PPTX</span>
            <span className="scoutiq-format-tag">TXT</span>
            <span className="scoutiq-format-tag">MD</span>
            <span className="scoutiq-format-tag">CSV</span>
          </div>

          <p className="scoutiq-maxsize">Max file size: 15MB</p>

          {dropErrors.length > 0 && (
            <div className="drop-errors" role="alert" aria-live="polite">
              {dropErrors.map(err => (
                <div key={err.id} className="drop-error-row">
                  <span className="drop-error-icon">&#x26A0;&#xFE0F;</span>
                  <span className="drop-error-text">
                    {err.name}: {err.message}
                  </span>
                  <button
                    type="button"
                    className="drop-error-close"
                    onClick={() => setDropErrors(prev => prev.filter(e => e.id !== err.id))}
                    aria-label="Dismiss"
                  >&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Standard layout: dropzone, then supported panel below
        <>
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? "dropzone-active" : ""}`}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <p className="dropzone-text">Drop files here...</p>
            ) : (
              <>
                <p className="dropzone-text">
                  Drag &amp; drop files here, or click the button below to select
                  files.
                </p>
                {selectedFiles.length === 0 && (
                  <div className="empty-state-hints">
                    <div className="hint-card">
                      <div className="hint-icon">📄</div>
                      <div className="hint-text">Upload PDFs/DOCX</div>
                    </div>
                    <div className="hint-divider">or</div>
                    <div className="hint-card">
                      <div className="hint-icon">🖼️</div>
                      <div className="hint-text">
                        Upload multiple images<br/>
                        <span className="hint-arrow">→</span> auto-stitch to PDF
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <button type="button" onClick={open} className="upload-btn" disabled={!user}>
              Select Files
            </button>
          </div>

          {dropErrors.length > 0 && (
            <div className="drop-errors" role="alert" aria-live="polite">
              {dropErrors.map(err => (
                <div key={err.id} className="drop-error-row">
                  <span className="drop-error-icon">⚠️</span>
                  <span className="drop-error-text">
                    {err.name}: {err.message}
                  </span>
                  <button
                    type="button"
                    className="drop-error-close"
                    onClick={() => setDropErrors(prev => prev.filter(e => e.id !== err.id))}
                    aria-label="Dismiss"
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div className="supported-files-panel" role="note" aria-label="Supported file types">
            <div className="sf-row">
              <div className="sf-col">
                <div className="sf-title">Supported (text extracted and chunked)</div>
                <div className="sf-badges">
                  <span className="sf-badge">PDF</span>
                  <span className="sf-badge">DOCX</span>
                  <span className="sf-badge">PPTX</span>
                  <span className="sf-badge">TXT</span>
                  <span className="sf-badge">Markdown</span>
                  <span className="sf-badge">CSV</span>
                  <span className="sf-badge">JSON</span>
                  <span className="sf-badge">HTML</span>
                  <span className="sf-badge">RTF</span>
                  <span className="sf-badge">Logs</span>
                  <span className="sf-badge">Source files</span>
                  <span className="sf-badge">PNG</span>
                  <span className="sf-badge">JPG</span>
                </div>
                <div className="sf-note">PDF must be text-based (not scanned/image-only). PPTX text from slide shapes only.</div>
              </div>
              <div className="sf-col">
                <div className="sf-title not">Not Supported</div>
                <div className="sf-badges">
                  <span className="sf-badge bad">DOC (legacy)</span>
                  <span className="sf-badge bad">XLS</span>
                  <span className="sf-badge bad">XLSX</span>
                </div>
              </div>
            </div>
            <div className="sf-foot">Max file size: 15MB.</div>
          </div>
        </>
      )}
      {/* Show only if we have files */}
      {selectedFiles.length > 0 && (
        <div className="file-list-container">
          <div className="filelist-header">
            <h3>Files to Upload</h3>
            {!endeavorId && courseOptions.length > 0 && (
              <div className="filelist-bulk-course">
                <svg className="filelist-bulk-course__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
                <span className="filelist-bulk-course__text">Tag all:</span>
                <select
                  className="filelist-bulk-course__select"
                  value={selectedCourseId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedCourseId(val);
                    setFileCourseIds(prev => {
                      const next = { ...prev };
                      selectedFiles.forEach(({ file }) => { next[file.name] = val; });
                      return next;
                    });
                  }}
                >
                  <option value="">No course</option>
                  {courseOptions.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          {/* Success card */}
          {stitchedResult && (
            <div className="stitch-success-card">
              <div className="success-icon">📄</div>
              <div className="success-content">
                <div className="success-title">{stitchedResult.filename}</div>
                <div className="success-meta">
                  <span>✓ {stitchedResult.pageCount} {stitchedResult.pageCount === 1 ? 'page' : 'pages'}</span>
                  {stitchedResult.ocrCharCount > 0 && (
                    <span>✓ Text extracted from {stitchedResult.pageCount} {stitchedResult.pageCount === 1 ? 'page' : 'pages'}</span>
                  )}
                </div>
              </div>
              <button 
                className="success-dismiss"
                onClick={() => setStitchedResult(null)}
                aria-label="Dismiss"
              >✕</button>
            </div>
          )}
          
          {/* Enhanced stitch detection callout */}
          {showStitchOption && (
            <div className="stitch-detection-callout">
              <div className="callout-header">
                <div className="callout-icon">🖼️</div>
                <div className="callout-text">
                  <div className="callout-title">Multiple Images Detected</div>
                  <div className="callout-subtitle">We'll combine these into a single searchable PDF</div>
                </div>
              </div>
              
              <div className="stitch-expanded">
                  {/* Thumbnail preview strip */}
                  {imageEntries.length > 0 && (
                    <div className="thumbnail-strip">
                      <div className="strip-label">Page Order:</div>
                      <div className="thumbnail-scroll">
                        {imageEntries.map(({file}, idx) => {
                          const uploading = uploadingFiles[file.name];
                          return (
                            <div key={file.name} className="thumbnail-card">
                              <div className="thumbnail-preview">
                                {imageThumbnails[file.name] ? (
                                  <img src={imageThumbnails[file.name]} alt="" />
                                ) : (
                                  <div className="thumbnail-placeholder">🖼️</div>
                                )}
                                <div className="thumbnail-badge">{idx + 1}</div>
                              </div>
                              <div className="thumbnail-name">{file.name.length > 12 ? file.name.slice(0, 12) + '...' : file.name}</div>
                              <div className="thumbnail-reorder">
                                <button
                                  type="button"
                                  className="reorder-btn"
                                  onClick={() => {
                                    if (idx === 0) return;
                                    setSelectedFiles(prev => {
                                      const arr = [...prev];
                                      [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]];
                                      return arr;
                                    });
                                  }}
                                  disabled={idx === 0 || uploading}
                                  title="Move up"
                                  aria-label="Move up"
                                >↑</button>
                                <button
                                  type="button"
                                  className="reorder-btn"
                                  onClick={() => {
                                    setSelectedFiles(prev => {
                                      if (idx === prev.length - 1) return prev;
                                      const arr = [...prev];
                                      [arr[idx+1], arr[idx]] = [arr[idx], arr[idx+1]];
                                      return arr;
                                    });
                                  }}
                                  disabled={idx === selectedFiles.length - 1 || uploading}
                                  title="Move down"
                                  aria-label="Move down"
                                >↓</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <div className="stitch-fields">
                    <div className="stitch-field">
                      <label className="stitch-label" htmlFor="bundleTitle">
                        Title <span aria-hidden="true">*</span>
                        <span className="help-tooltip" title="Name for your combined PDF document">ⓘ</span>
                      </label>
                      <input id="bundleTitle" className="stitch-input" value={bundleTitle} onChange={e=>setBundleTitle(e.target.value)} placeholder="e.g. Lecture Slides (Scanned)" />
                    </div>
                    <div className="stitch-field">
                      <label className="stitch-label" htmlFor="bundleDescription">Description</label>
                      <input id="bundleDescription" className="stitch-input" value={bundleDescription} onChange={e=>setBundleDescription(e.target.value)} placeholder="Optional description" />
                    </div>
                    <div className="stitch-field stitch-ocr">
                      <label className="stitch-ocr-label">
                        <input type="checkbox" className="ui-checkbox" checked={bundleOCR} onChange={e=>setBundleOCR(e.target.checked)} />
                        <span>Run OCR</span>
                        <span className="help-tooltip" title="Extracts text from images to make your document searchable">ⓘ</span>
                      </label>
                    </div>
                  </div>
                  
                  {bundleStatus && (
                    <div className="stitch-status-container">
                      <div className={`stitch-status phase-${bundleStatus.phase}`} role={bundleStatus.phase==='error' ? 'alert' : 'status'}>
                        {bundleStatus.phase === 'uploading' && (
                          <span className="loading-spinner" aria-hidden="true"></span>
                        )}
                        {bundleStatus.message}
                      </div>
                      {bundleStatus.phase === 'uploading' && bundleProgress.phase > 0 && (
                        <div className="progress-bar-container">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${(bundleProgress.phase / bundleProgress.total) * 100}%` }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
            </div>
          )}

          {/* File cards */}
          <div className="filelist-cards">
            {selectedFiles.map(({ file }) => {
              const uploading = uploadingFiles[file.name];
              const error = errorMessages[file.name];
              const showPerFile = !endeavorId && courseOptions.length > 0;
              const ext = file.name.split('.').pop()?.toUpperCase() || '';
              const courseName = fileCourseIds[file.name] // eslint-disable-line no-unused-vars
                ? courseOptions.find(c => String(c.id) === String(fileCourseIds[file.name]))?.name
                : null;

              return (
                <div key={file.name} className={`filelist-card${uploading ? ' is-uploading' : ''}`}>
                  <div className="filelist-card__icon" data-ext={ext}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="filelist-card__ext">{ext}</span>
                  </div>
                  <div className="filelist-card__body">
                    <div className="filelist-card__name" title={file.name}>{file.name}</div>
                    <div className="filelist-card__meta">
                      {showPerFile && (
                        <div className="filelist-card__course">
                          <svg className="filelist-card__course-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                          </svg>
                          <select
                            className="filelist-card__course-select"
                            value={fileCourseIds[file.name] || ''}
                            onChange={(e) => setFileCourseIds(prev => ({ ...prev, [file.name]: e.target.value }))}
                            disabled={uploading}
                            aria-label={`Course for ${file.name}`}
                          >
                            <option value="">Assign course…</option>
                            {courseOptions.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {error && <span className="filelist-card__error">{error}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="filelist-card__remove"
                    onClick={() => removeFile(file.name)}
                    disabled={uploading}
                    aria-label={`Remove ${file.name}`}
                    title="Remove"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
          {/* Upload All button */}
          <button
            className="upload-all-btn"
            onClick={uploadFiles}
            disabled={selectedFiles.length === 0 || Object.keys(uploadingFiles).length > 0 || !user}
          >
            Upload All
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

export default FileUpload;
